import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import BasicTable, { NUMERIC_CELL_SX } from '../../../../components/BasicTable/BasicTable';
import ListToolbar from '../../../../components/ListToolbar/ListToolbar';
import RowActionsMenu from '../../../../components/RowActionsMenu/RowActionsMenu';
import FilteredEmptyState from '../../../../components/FilteredEmptyState';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericInfoModal from '../../../../components/GenericInfoModal';
import BillingItemModal from './BillingItemModal';
import { ReactComponent as AddIcon } from '../../../../assets/add-icon.svg';
import { ITableHeadings } from '../../../../common/interfaces/common.interface';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { useListUrlState } from '../../../../utilities/hooks/useListUrlState';
import { ActionMode, GeneralStatusEnum } from '../../../../common/enums/common.enum';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { BILLING_ITEM_TYPE_LABELS, BillingItemTypeEnum } from '../../../../common/enums/invoicing.enum';
import { BillingItemDto } from '../../../../common/interfaces/invoicing.interface';
import { deleteBillingItem, getBillingItems, getBillingSettings } from '../../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../../theme';
export const BILLING_ITEMS_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 0, value: '', label: 'billing.items.source', sortable: false, width: 90 },
  { id: 1, value: '', label: 'common.name', sortable: false, width: '18%' },
  { id: 2, value: '', label: 'billing.lines.invoiceLabel', sortable: false, width: '24%' },
  { id: 3, value: '', label: 'billing.items.calculation', sortable: false },
  { id: 4, value: '', label: 'billing.items.unit', sortable: false },
  { id: 5, value: '', label: 'billing.items.netPrice', sortable: false, align: 'right' },
  { id: 6, value: '', label: 'billing.lines.vat', sortable: false },
  { id: 7, value: '', label: 'common.status', sortable: false },
  { id: 8, value: '', label: '', sortable: false, width: 56 }
];

interface BillingItemsTabProps {
  kindergartenId: string;
  /** After anything is saved or removed, so the setup cards above can recount. */
  onChanged?: () => void;
}

/**
 * The catalogue of things a kindergarten can bill for. Items carry the dual name from
 * the spec: an internal one for staff, and the label the parent reads.
 */
const BillingItemsTab: React.FC<BillingItemsTabProps> = ({ kindergartenId, onChanged }) => {
  const { t } = useTranslation();
  const list = useListUrlState();
  const { search, page, perPage } = list;
  const types = list.values('type') as BillingItemTypeEnum[];
  const showInactive = list.flag('inactive');
  const filtered = !!search || !!types.length || showInactive;
  const clearFilters = () => list.clearFilters(['type', 'inactive']);

  const [items, setItems] = useState<BillingItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loader, setLoader] = useState(false);
  const [vatApplicable, setVatApplicable] = useState(false);
  const [editing, setEditing] = useState<BillingItemDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState<BillingItemDto | null>(null);

  const loadItems = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getBillingItems(kindergartenId, {
        page,
        perPage,
        search: search || undefined,
        types: types.length ? types : undefined,
        status: showInactive ? undefined : GeneralStatusEnum.ACTIVE
      });
      setItems(result.results ?? []);
      setTotal(result.paginationInfo?.totalItems ?? 0);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.items.loadFailed'));
    } finally {
      setLoader(false);
    }
    // the type list is rebuilt from the URL on every render; its joined form is the dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, page, perPage, search, types.join(), showInactive]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    getBillingSettings(kindergartenId)
      .then(result => setVatApplicable(!!result.results?.isInVatSystem))
      .catch(() => undefined);
  }, [kindergartenId]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteBillingItem(kindergartenId, toDelete.id);
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.items.removed'));
      setToDelete(null);
      loadItems();
      onChanged?.();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.items.removeFailed'));
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 24, color: 'text.primary', fontFamily: 'Poppins' }}>{t('billing.items.title')}</Typography>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          startIcon={<AddIcon />}
          label={t('billing.items.create')}
          onClick={() => setCreating(true)}
        />
      </Stack>

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4 }}>
        <ListToolbar
          search={{ value: search, onChange: list.setSearch, placeholder: t('billing.items.searchNameOrLabel') }}
          filters={[
            {
              kind: 'multi',
              key: 'type',
              label: t('billing.items.calculation'),
              options: Object.values(BillingItemTypeEnum).map(value => ({ value, label: t(BILLING_ITEM_TYPE_LABELS[value]) })),
              selected: types,
              onChange: values => list.setValues('type', values)
            },
            { kind: 'toggle', key: 'inactive', label: t('billing.items.showInactive'), active: showInactive, onChange: on => list.setFlag('inactive', on) }
          ]}
          onClearAll={clearFilters}
        />

        <BasicTable
          headings={BILLING_ITEMS_TABLE_HEADINGS}
          loader={loader}
          {...list.tableProps(total)}
          emptyState={filtered ? <FilteredEmptyState onClear={clearFilters} /> : undefined}
        >
          {items.map(item => (
            <TableRow
              key={item.id}
              hover
              tabIndex={0}
              sx={{ cursor: 'pointer' }}
              onClick={() => setEditing(item)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setEditing(item);
                }
              }}
            >
              <TableCell>
                <Tooltip title={item.isPlatformItem ? t('billing.items.platformSourceHint') : t('billing.items.ownSourceHint')}>
                  <Chip
                    size="small"
                    variant={item.isPlatformItem ? 'filled' : 'outlined'}
                    label={item.isPlatformItem ? t('billing.items.sourcePlatform') : t('billing.items.sourceOwn')}
                    color={item.isPlatformItem ? 'primary' : 'default'}
                  />
                </Tooltip>
              </TableCell>
              <TableCell>
                {item.name}
                {item.isPlatformItem && !!item.overriddenFields.length && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {t('billing.items.adjustedFields', {
                      fields: item.overriddenFields.map(field => t(`billing.items.field.${field}`, { defaultValue: field })).join(', ')
                    })}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{item.pdfLabel}</TableCell>
              <TableCell>{t(BILLING_ITEM_TYPE_LABELS[item.type])}</TableCell>
              <TableCell>{item.unit ?? '—'}</TableCell>
              <TableCell sx={NUMERIC_CELL_SX}>{formatMoney(item.unitPriceNet)}</TableCell>
              <TableCell>{vatApplicable ? `${item.vatRate}%` : '—'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`enums.generalStatus.${item.status}`)}
                  color={item.status === GeneralStatusEnum.ACTIVE ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>
                {/* the row opens the editor; a platform item is adjusted, never removed from a kindergarten */}
                <RowActionsMenu
                  name={item.name}
                  actions={[{ label: t('billing.items.remove'), hidden: item.isPlatformItem, danger: true, onClick: () => setToDelete(item) }]}
                />
              </TableCell>
            </TableRow>
          ))}
        </BasicTable>
      </Box>

      {creating && (
        <BillingItemModal
          mode={ActionMode.CREATE}
          kindergartenId={kindergartenId}
          vatApplicable={vatApplicable}
          onClose={() => setCreating(false)}
          onSaved={() => {
            loadItems();
            onChanged?.();
          }}
        />
      )}
      {editing && (
        <BillingItemModal
          mode={ActionMode.EDIT}
          kindergartenId={kindergartenId}
          vatApplicable={vatApplicable}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            loadItems();
            onChanged?.();
          }}
        />
      )}
      {toDelete && (
        <GenericInfoModal
          open
          onClose={() => setToDelete(null)}
          onConfirm={handleDelete}
          label={t('billing.items.remove')}
          message={t('billing.items.removeConfirm')}
          confirmLabel={t('common.remove')}
          entityName={toDelete.name}
        />
      )}
    </Box>
  );
};

export default BillingItemsTab;
